// @flow

import type {
  KPIs,
  ShoppingCenterGemini,
  StoreGemini,
  VisioglobeUnitsMap,
  VisioglobeUnitsOk,
} from '../../../../types/geminiType';
import { getRandomColor } from '../../../../utils/utils';
import type { MapViewer } from '../../../../types/VisioglobeType';
import { LU, MSU, reconciliationStates, SU } from '../../../../const';
import { dwellPercent, ocr } from './geminiConst';

export function sumKpis(kpis: KPIs[]): KPIs {
  if (!kpis || kpis.length === 0) {
    // $FlowFixMe
    return null;
  }

  let glaCoef = 0;
  const res = {};
  kpis.forEach((kpi) => {
    if (kpi) {
      Object.keys(kpi).forEach((key) => {
        if (kpi[key]) {
          if (!res[key]) {
            res[key] = 0;
          }
          if ((key === ocr || key === dwellPercent) && kpi.glaWithSst) {
            glaCoef += kpi.glaWithSst;
            res[key] += (kpi[key] * kpi.glaWithSst);
          } else {
            res[key] += kpi[key];
          }
        }
      });
    }
  });
  if (res.ocr) {
    res.ocr /= glaCoef;
  }
  if (res.dwellPercent) {
    res.dwellPercent /= glaCoef;
  }
  return res;
}

export function computeColorZone(stores: StoreGemini[]) {
  const res = {};
  stores.forEach((store) => {
    if (!!store.zoneName && !res[store.zoneName]) {
      res[store.zoneName] = getRandomColor();
    }
  });
  return res;
}

export function initMap(
  stores: StoreGemini[],
  mapViewer: MapViewer,
) {
  mapViewer.loadLabelNoColor(stores, {});
}

export function glaDivider(
  kpiSelected: string,
  shoppingCentre: ShoppingCenterGemini,
  storeSize: string,
): number {
  let kpi;
  switch (storeSize) {
    case LU.lowercase:
      kpi = shoppingCentre.luAvg.sort((a, b) => b.year - a.year)[0];
      break;
    case SU.lowercase:
      kpi = shoppingCentre.suAvg.sort((a, b) => b.year - a.year)[0];
      break;
    case MSU.lowercase:
      kpi = shoppingCentre.msuAvg.sort((a, b) => b.year - a.year)[0];
      break;
    default:
      kpi = shoppingCentre.avg.sort((a, b) => b.year - a.year)[0];
      break;
  }
  if (kpiSelected === 'salesNum') {
    return kpi.glaDen;
  }
  return kpi.glaWithSst;
}

export function computeOcupancyCost(kpi: KPIs) {
  if (!kpi) {
    return 0;
  }
  return (kpi.invMgr ? kpi.invMgr : 0) +
    (kpi.invSch ? kpi.invSch : 0) +
    (kpi.invSbr ? kpi.invSbr : 0) +
    (kpi.invRet ? kpi.invRet : 0) +
    (kpi.invMco ? kpi.invMco : 0) +
    (kpi.invMwo ? kpi.invMwo : 0) +
    (kpi.invLsw ? kpi.invLsw : 0) +
    (kpi.invRie ? kpi.invRie : 0) +
    (kpi.invPal ? kpi.invPal : 0) +
    (kpi.invFra ? kpi.invFra : 0) +
    (kpi.invAbt ? kpi.invAbt : 0) +
    (kpi.invPli ? kpi.invPli : 0) +
    (kpi.invAdm ? kpi.invAdm : 0) +
    (kpi.invCtr ? kpi.invCtr : 0) +
    (kpi.invOin ? kpi.invOin : 0) +
    (kpi.invAnn ? kpi.invAnn : 0);
}

export const getWeighingAvgByZoneName = (visioglobeUnits: VisioglobeUnitsOk[], zoneName: string) => {
  const storeList = visioglobeUnits.filter(store =>
    store.zoneName === zoneName && store.localWeighting !== null);
  return (
    storeList.reduce((acc, store) => acc + Number(store.localWeighting), 0) / storeList.length
  ).toFixed(2);
};

export function getCurrentQuartil(shoppingCenterData: ShoppingCenterGemini, categorie: string) {
  switch (categorie) {
    case SU.lowercase:
      return shoppingCenterData
        .suQuartils;
    case LU.lowercase:
      return shoppingCenterData
        .luQuartils;
    case MSU.lowercase:
      return shoppingCenterData
        .msuQuartils;
    default:
      return shoppingCenterData
        .quartils;
  }
}

export function getOkUnits(visioglobeUnitsMap: VisioglobeUnitsMap): VisioglobeUnitsOk[] {
  return visioglobeUnitsMap.ok.concat(visioglobeUnitsMap.mapped);
}

export function getAllDisplayableUnits(visioglobeUnitsMap: VisioglobeUnitsMap): boolean {
  return Object.entries(visioglobeUnitsMap)
    .filter(entry => entry[0] !== 'mercuryOnly')
    .flatMap(entry => entry[1]);
}

export function getAllDisplayableUnitsWithBestRetailer(visioglobeUnitsMap: VisioglobeUnitsMap, codstrToRename: number, retailerName: string): boolean {
  return Object.entries(visioglobeUnitsMap)
    .filter(entry => entry[0] !== 'mercuryOnly')
    .flatMap(entry => entry[1])
    .map((entry) => {
      if (entry.codstr === codstrToRename) {
        entry.retailerName = retailerName;
        entry.storeName = retailerName;
      }
      return entry;
    });
}

export function getAllIdVisioglobesUnits(visioglobeUnitsMap: VisioglobeUnitsMap): boolean {
  return Object.entries(visioglobeUnitsMap)
    .flatMap(entry => entry[1])
    .flatMap(entry => entry.idVisioglobes);
}

export function isMappable(storeState: string): boolean {
  return storeState === reconciliationStates.MULTIPLE_MERCURY ||
    storeState === reconciliationStates.MULTIPLE_LUX ||
    storeState === reconciliationStates.LUX_ONLY ||
    storeState === reconciliationStates.EMPTY_UNIT ||
    storeState === reconciliationStates.RETAILER_MISSING;
}

export function isStoreDisplayable(storeState: string): boolean {
  return storeState === reconciliationStates.OK
    || storeState === reconciliationStates.MAPPED
    || storeState === reconciliationStates.EMPTY_UNIT
    || storeState === reconciliationStates.LUX_ONLY
    || storeState === reconciliationStates.RETAILER_MISSING
    || storeState === reconciliationStates.MULTIPLE_LUX
    || storeState === reconciliationStates.MULTIPLE_MERCURY;
}

export function isStoreStateHistoryAndNotReplaced(
  storeState: string,
  idVisioglobes: string[],
  allUsedVisioglobes: string[],
): boolean {
  if (storeState === reconciliationStates.HISTORY) {
    return !allUsedVisioglobes.includes(idVisioglobes);
  }
  return false;
}

